import { useState } from "react";
import { message } from "antd";
import { getBulkStatusDocsInfo, createBulkStatusDocs } from "../../../apis/account.api";
import { BulkDocsInfoResult, BulkStatusDocResult, POStatusDocResult } from "../../../interfaces/account.interface";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";

export const useAccount = () => {
  const { hasPermission } = usePermissions();
  const [poNumbers, setPoNumbers] = useState<string[]>([]);
  const [allDocs, setAllDocs] = useState(false);
  const [statusOverride, setStatusOverride] = useState<"dispatched" | "delivered" | null>("dispatched");

  const [checkingDocs, setCheckingDocs] = useState(false);
  const [docInfo, setDocInfo] = useState<BulkDocsInfoResult | null>(null);

  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<BulkStatusDocResult | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  const handleCheckDocs = async () => {
    if (!poNumbers.length) {
      message.warning("Please enter at least one PO number.");
      return;
    }
    setCheckingDocs(true);
    setDocInfo(null);
    const data = await getBulkStatusDocsInfo(poNumbers);
    if (data) setDocInfo(data);
    setCheckingDocs(false);
  };

  const handleProcess = async () => {
    if (!hasPermission("invoice.push")) {
      message.error("You don't have permission to process documents.");
      return;
    }
    if (!poNumbers.length) {
      message.warning("Please enter at least one PO number.");
      return;
    }

    const batch = poNumbers;

    setProcessing(true);
    setResults(null);
    setProgress({ current: 0, total: batch.length });

    const data = await createBulkStatusDocs({
      po_numbers: batch,
      all_docs: allDocs,
      ...(statusOverride ? { status_override: statusOverride } : {}),
    });

    if (data) {
      setResults(data);
    } else {
      setResults({
        success: false,
        total: batch.length,
        success_count: 0,
        failed_count: batch.length,
        results: batch.map((po) => ({
          po_number: po,
          success: false,
          documents: [{ doc: "N/A", success: false, message: "Request failed" }],
        })),
      });
    }

    setPoNumbers([]);
    setProgress(null);
    setProcessing(false);
  };

  return {
    poNumbers,
    setPoNumbers,
    allDocs,
    setAllDocs,
    statusOverride,
    setStatusOverride,
    checkingDocs,
    docInfo,
    processing,
    results,
    progress,
    handleCheckDocs,
    handleProcess,
  };
};
